import React from 'react';
import useScrollReveal from '../hooks/useScrollReveal';
import { HiLocationMarker } from 'react-icons/hi';

const locations = [
  { number: '20', unit: 'Mins', place: 'Airport', icon: '✈️' },
  { number: '15', unit: 'Mins', place: 'Mumbai Trans Harbour Link (MTHL)', icon: '🌉' },
  { number: '10', unit: 'Mins', place: 'JNPT Port & Logistics Hub', icon: '🚢' },
  { number: '05', unit: 'Mins', place: 'Proposed Metro Station', icon: '🚇' },
  { number: '10', unit: 'Mins', place: 'Railway Station', icon: '🚂' },
  { number: '40', unit: 'Mins', place: 'Mumbai CST via MTHL', icon: '🏙️' },
];

const LocationAdvantages = () => {
  const [headRef, headVisible] = useScrollReveal();
  const [gridRef, gridVisible] = useScrollReveal({ threshold: 0.1 });
  const [mapRef, mapVisible] = useScrollReveal({ threshold: 0.2 });

  return (
    <section id="location" className="py-24 lg:py-32 bg-white relative border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header — centered */}
        <div ref={headRef} className={`text-center max-w-2xl mx-auto mb-16 transition-all duration-700 ${headVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block text-[11px] tracking-[0.3em] uppercase font-semibold text-accent-500 mb-5">
            Strategic Connectivity
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading text-dark-800 leading-tight mb-5">
            Location{' '}
            <span className="italic text-primary-600">Advantages</span>
          </h2>
          <p className="text-dark-400 text-base leading-relaxed font-light">
            NAINA & KSC enjoy unmatched connectivity through mega infrastructure projects.
          </p>
        </div>

        {/* Location cards — flowing grid */}
        <div ref={gridRef} className="flex flex-wrap justify-center gap-3 mb-10">
          {locations.map((loc, i) => (
            <div
              key={i}
              className={`w-[calc(50%-6px)] sm:w-[calc(33.333%-8px)] lg:w-[calc(25%-9px)] bg-gray-50 rounded-2xl p-5 group cursor-default hover:bg-primary-50 border border-transparent hover:border-primary-100 transition-all duration-500 ${
                gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: gridVisible ? `${i * 60}ms` : '0ms' }}
            >
              <div className="text-lg mb-3">{loc.icon}</div>
              <div className="flex items-baseline gap-1 mb-1.5">
                <span className="text-2xl lg:text-3xl font-heading text-primary-600 group-hover:text-accent-500 transition-colors duration-500 leading-none">
                  {loc.number}
                </span>
                <span className="text-[10px] text-dark-300 uppercase tracking-[0.15em] font-semibold">
                  {loc.unit}
                </span>
              </div>
              <p className="text-xs text-dark-500 font-medium leading-snug">{loc.place}</p>
            </div>
          ))}
        </div>

        {/* Map + image row */}
        <div ref={mapRef} className={`grid lg:grid-cols-2 gap-4 transition-all duration-700 ${mapVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="relative rounded-2xl overflow-hidden group h-[280px] lg:h-[320px]">
            <img
              src="https://images.unsplash.com/photo-1570168007204-dfb528c6958f?w=800&q=80"
              alt="Mumbai infrastructure aerial view"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[2000ms]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 flex items-center gap-3 bg-white/95 backdrop-blur-sm rounded-xl py-3 px-5 shadow-lg">
              <HiLocationMarker className="text-primary-600 text-lg" />
              <div>
                <p className="text-sm font-heading text-dark-800">NAINA & KSC, Navi Mumbai</p>
                <p className="text-[10px] text-dark-400">MahaMumbai Region, Maharashtra</p>
              </div>
            </div>
          </div>

          <div className="rounded-2xl overflow-hidden border border-gray-100 h-[280px] lg:h-[320px]">
            <iframe
              title="Location Map"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d120698.28!2d73.05!3d19.03!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7e8aaaaaaaa%3A0xbbbbbbbbbbbb!2sNAINA%20Region%2C%20Navi%20Mumbai!5e0!3m2!1sen!2sin!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default LocationAdvantages;
